import { useState, useEffect } from "react";
import CurrentBalance from "./components/CurrentBalance";
import RecurringTransactions from "./components/RecurringTransactions";
import TransactionTimeline from "./components/TransactionTimeline";
import OneTimeTransactions from "./components/OneTimeTransactions";
import { RecurringTransaction, OneTimeTransaction } from "./types";
import { Card, CardHeader, CardTitle, CardContent } from "./components/ui/card";
import BalanceGraph from "./components/BalanceGraph";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "./components/ui/resizable";

function App() {
  const [currentBalance, setCurrentBalance] = useState<number>(() => {
    const saved = localStorage.getItem("currentBalance");
    return saved ? Number(saved) : 0;
  });

  const [startDate, setStartDate] = useState<Date>(() => {
    const saved = localStorage.getItem("startDate");
    return saved ? new Date(saved) : new Date();
  });

  const [recurringTransactions, setRecurringTransactions] = useState<
    RecurringTransaction[]
  >(() => {
    const saved = localStorage.getItem("recurringTransactions");
    if (!saved) return [];
    return JSON.parse(saved).map((t: any) => ({
      ...t,
      startDate: new Date(t.startDate),
    }));
  });

  const [oneTimeTransactions, setOneTimeTransactions] = useState<
    OneTimeTransaction[]
  >(() => {
    const saved = localStorage.getItem("oneTimeTransactions");
    if (!saved) return [];
    return JSON.parse(saved).map((t: any) => ({
      ...t,
      date: new Date(t.date),
    }));
  });

  useEffect(() => {
    localStorage.setItem("currentBalance", currentBalance.toString());
  }, [currentBalance]);

  useEffect(() => {
    localStorage.setItem("startDate", startDate.toISOString());
  }, [startDate]);

  useEffect(() => {
    localStorage.setItem(
      "recurringTransactions",
      JSON.stringify(recurringTransactions)
    );
  }, [recurringTransactions]);

  useEffect(() => {
    localStorage.setItem(
      "oneTimeTransactions",
      JSON.stringify(oneTimeTransactions)
    );
  }, [oneTimeTransactions]);

  return (
    <div className="min-h-screen dark flex flex-col p-4">
      <h1 className="text-2xl font-bold text-center mb-4">💸</h1>

      <ResizablePanelGroup direction="vertical" className="flex-1">
        <ResizablePanel defaultSize={25} minSize={15}>
          <Card className="h-full">
            <CardContent className="h-[calc(100%-1rem)] pt-4">
              <BalanceGraph
                currentBalance={currentBalance}
                recurringTransactions={recurringTransactions}
                oneTimeTransactions={oneTimeTransactions}
                startDate={startDate}
              />
            </CardContent>
          </Card>
        </ResizablePanel>
        <ResizableHandle className="my-2" withHandle />
        <ResizablePanel defaultSize={75}>
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel defaultSize={50}>
              <div className="h-full overflow-y-auto space-y-4">
                <Card className="mb-4">
                  <CardHeader>
                    <CardTitle>Starting Balance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CurrentBalance
                      balance={currentBalance}
                      onBalanceChange={setCurrentBalance}
                      date={startDate}
                      onDateChange={setStartDate}
                    />
                  </CardContent>
                </Card>
                <Card className="mb-4">
                  <CardHeader>
                    <CardTitle>Recurring Transactions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RecurringTransactions
                      transactions={recurringTransactions}
                      onTransactionsChange={setRecurringTransactions}
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>One-Time Transactions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <OneTimeTransactions
                      transactions={oneTimeTransactions}
                      onTransactionsChange={setOneTimeTransactions}
                    />
                  </CardContent>
                </Card>
              </div>
            </ResizablePanel>
            <ResizableHandle className="mx-2" withHandle />
            <ResizablePanel defaultSize={50}>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle>Timeline</CardTitle>
                </CardHeader>
                <CardContent className="h-[calc(100%-5rem)] overflow-y-auto">
                  <TransactionTimeline
                    startingBalance={currentBalance}
                    startingDate={startDate}
                    recurringTransactions={recurringTransactions}
                    oneTimeTransactions={oneTimeTransactions}
                    startDate={startDate}
                  />
                </CardContent>
              </Card>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

export default App;
